import { RefObject, useEffect, useRef } from 'react'

import { Timeout } from '../types/timeout'

type ScrollToElementTarget = HTMLElement | RefObject<HTMLElement | null> | null

const getScrollElement = (target?: ScrollToElementTarget): HTMLElement | null => {
  if (!target) {
    return null
  }

  if (!(target instanceof HTMLElement) && 'current' in target) {
    return target.current
  }

  return target
}

export const useScrollToElement = (target?: ScrollToElementTarget, shouldScroll?: boolean): void => {
  const isRenderRef = useRef(false) // mechanism for not animating the scroll when it's initially rendered
  const mountTimeoutRef = useRef<Timeout>()

  useEffect(() => {
    mountTimeoutRef.current = setTimeout(() => {
      isRenderRef.current = true
    }, 0)

    return () => {
      if (mountTimeoutRef.current) {
        clearTimeout(mountTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!shouldScroll) {
      return
    }

    const behavior = isRenderRef.current ? 'smooth' : 'auto'

    // containers such as Dropdown size themselves in a parent effect, which runs after this one,
    // so the scroll has to wait for the surrounding layout to settle
    const animationFrameId = requestAnimationFrame(() => {
      getScrollElement(target)?.scrollIntoView({ behavior, block: 'center' })
    })

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [target, shouldScroll])
}
